import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, delay, filter, mapTo, share } from 'rxjs/operators';
import { AuthUserHttpService } from 'src/app/http-services/auth-user-http.service';
import { isNotEmpty } from 'src/app/shared/rx.helpers';
import {
  aclByRoleTypeToEntityResourceMapper,
  aclResourcesToMap
} from 'src/app/shared/types/acl.mappers';
import {
  AclBusinessRoleType,
  AclEntityResourceBitMap,
  AclResourcesBitMap,
  IAclRaw
} from '../shared/types/acl.types';

@Injectable({
  providedIn: 'root'
})
export class AclService {
  private readonly authUserHttpService: AuthUserHttpService;

  private readonly _acl$: BehaviorSubject<IAclRaw|null> = new BehaviorSubject<IAclRaw|null>(null);
  private readonly _aclMapGeneral$: BehaviorSubject<AclResourcesBitMap|null> = new BehaviorSubject<AclResourcesBitMap|null>(null);
  private readonly _aclMapByAccount$: BehaviorSubject<AclEntityResourceBitMap|null> = new BehaviorSubject<AclEntityResourceBitMap|null>(null);
  private readonly _aclMapByOrg$: BehaviorSubject<AclEntityResourceBitMap|null> = new BehaviorSubject<AclEntityResourceBitMap|null>(null);
  private readonly _isAclLoaded$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor() {
    this.authUserHttpService = inject(AuthUserHttpService);
  }

  public get aclMapGeneral$(): Observable<AclResourcesBitMap> {
    return this._aclMapGeneral$
      .pipe(filter(isNotEmpty));
  }

  public get aclMapByAccount$(): Observable<AclEntityResourceBitMap | null> {
    return this._aclMapByAccount$.asObservable();
  }

  public get aclMapByOrg$(): Observable<AclEntityResourceBitMap | null> {
    return this._aclMapByOrg$.asObservable();
  }

  public get isAclLoaded$(): Observable<boolean> {
    return this._isAclLoaded$.asObservable();
  }

  public get aclSnapshot(): IAclRaw | null {
    return this._acl$.getValue();
  }

  public get aclMapGeneralSnapshot(): AclResourcesBitMap|null {
    return this._aclMapGeneral$.getValue();
  }

  public get aclMapByOrgSnapshot(): AclEntityResourceBitMap|null {
    return this._aclMapByOrg$.getValue();
  }

  public get aclMapByAccountSnapshot(): AclEntityResourceBitMap|null {
    return this._aclMapByAccount$.getValue();
  }

  public terminateLoading(): void {
    this._isAclLoaded$.next(true);
  }

  public fetchAcl(): void {
    this.authUserHttpService.getAcl()
      .pipe(
        /**
         * @todo: Check if we really need it
         */
        delay(2000),
      )
      .subscribe(
        data => {
          this._acl$.next(data);
          this._aclMapGeneral$.next(aclResourcesToMap(data.generalResources));
          this._aclMapByOrg$.next(aclByRoleTypeToEntityResourceMapper(
            data.groupAuthDto.filter(group => group.roleType === AclBusinessRoleType.ORGANIZATION)
          ));
        },
        error => this._acl$.error(error),
      )
      .add(() => this._isAclLoaded$.next(true));
  }

  public fetchAclForAccount(accountId: string): Observable<void> {
    const observable$ = this.authUserHttpService.getAclByAccount(accountId)
      .pipe(
        catchError(error => {
          this._acl$.error(error);
          return of(null);
        }),
        share(),
      );

    observable$
      .pipe(
        filter(isNotEmpty),
      )
      .subscribe(
        data => {
          this._aclMapByAccount$.next(aclByRoleTypeToEntityResourceMapper(data))
        },
        error => this._acl$.error(error),
      );

    return observable$.pipe(mapTo(void 0));
  }
}
